/**
 * Centralized API configuration module
 * Handles dynamic API URL resolution for different deployment scenarios
 */

/**
 * Check if the current hostname is a localhost address.
 * @param {string} hostname - The hostname to check
 * @returns {boolean} True if the hostname is localhost
 */
function isLocalhostHostname(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Check if a URL is configured for localhost.
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL contains localhost
 */
function isLocalhostUrl(url) {
  return url.includes('://localhost:') || url.includes('://127.0.0.1:');
}

/**
 * Resolve a URL dynamically based on current browser location.
 * If the frontend is accessed via a network IP but the URL is configured for localhost,
 * replace localhost with the current host's IP/hostname.
 * Also upgrades HTTP to HTTPS if the page is loaded via HTTPS.
 *
 * @param {string} configuredUrl - The configured URL (may contain localhost)
 * @returns {string} The resolved URL
 */
function resolveUrl(configuredUrl) {
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    const isPageSecure = window.location.protocol === 'https:';
    let resolvedUrl = configuredUrl;

    // Check if we're accessing from a non-localhost address but URL is configured for localhost
    if (!isLocalhostHostname(currentHost) && isLocalhostUrl(resolvedUrl)) {
      // Replace localhost with the current host to allow cross-network access
      resolvedUrl = resolvedUrl
        .replace('://localhost:', `://${currentHost}:`)
        .replace('://127.0.0.1:', `://${currentHost}:`);
    }

    // Upgrade to HTTPS if page is loaded via HTTPS (avoid mixed content)
    if (isPageSecure && !isLocalhostHostname(currentHost)) {
      resolvedUrl = resolvedUrl.replace(/^http:/, 'https:');
    }

    return resolvedUrl;
  }

  return configuredUrl;
}

/**
 * Get the API base URL.
 * 
 * This function handles the common issue where frontend is accessed via network IP
 * (e.g., http://192.168.1.100:3000) but the API URL is configured as localhost.
 * 
 * Resolution priority:
 * 1. If REACT_APP_API_URL is explicitly set and NOT localhost, use it directly
 * 2. If the current page is accessed via a network IP but API is configured as localhost,
 *    replace localhost with the current host's IP/hostname
 * 3. Otherwise, use the configured or default localhost URL
 * 
 * @returns {string} The API base URL (without trailing slash)
 */
export function getApiUrl() {
  // Sale SIEMPRE de la misma URL que usa apiClient (getBackendUrl() + '/api').
  // Con dos variables distintas (REACT_APP_API_URL y REACT_APP_BACKEND_URL),
  // AuthContext podía hablar con un backend y axios con otro: si el primero no
  // respondía, la sesión se cerraba sola (auditoría vibe 2026-10-07, FE-11).
  return `${getBackendUrl()}/api`;
}

/**
 * Get the backend base URL (without /api path).
 * Uses the same dynamic resolution logic as getApiUrl.
 * 
 * @returns {string} The backend base URL (without trailing slash or /api)
 */
export function getBackendUrl() {
  const configuredUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  return resolveUrl(configuredUrl);
}

// Export constants for backward compatibility
// NOTE: These constants are computed at module load time and won't adapt to
// dynamic hostname changes during runtime. For dynamic behavior, use the
// exported functions (getApiUrl, getBackendUrl) instead.
// No hay canal WebSocket: el backend no expone ninguno y la consola refresca
// por sondeo HTTP (vibe-audit FE-22 / RP-05).
export const API_URL = getApiUrl();
export const BACKEND_URL = getBackendUrl();
